import React from "react";
import type { MigrationObject } from "../../API/Models";

export const numberFormatter = new Intl.NumberFormat();
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
export type StatusFilter = "all" | "supported" | "broken";
export type RedirectFilter = "all" | "redirected";
export type SkipFilter = "all" | "skipped" | "active";

export type MigrationUploadObject = MigrationObject & { title?: string | null };

export const PRIMARY_SITE_BASE_URL = "https://www.apa.cd";
export const REFERENCE_SITE_BASE_URL = "https://apa-parts.production.pulsarinteractive.io/";

const formatHostLabel = (url: string) => {
	try {
		const host = new URL(url).hostname.replace(/^www\./i, "");
		return host.replace(/\.[^.]+$/, "");
	} catch {
		return url.replace(/\.[^.]+$/, "");
	}
};

export const PRIMARY_SITE_LABEL = formatHostLabel(PRIMARY_SITE_BASE_URL);
export const REFERENCE_SITE_LABEL = formatHostLabel(REFERENCE_SITE_BASE_URL);

export const MIGRATION_FIELD_MAP: Record<string, keyof MigrationUploadObject> = {
	id: "id",
	url: "url",
	requestedurl: "requestedUrl",
	requesturl: "requestedUrl",
	pagetitle: "pageTitle",
	contentheading: "contentHeading",
	title: "title",
	file: "file",
	headings: "headings",
	documentlinks: "documentLinks",
	documentlink: "documentLinks",
	downloadeddocuments: "downloadedDocuments",
	downloadedfiles: "downloadedDocuments",
	crawledat: "crawledAt",
	issupported: "isSupported",
	isbroken: "isBroken",
	isredirected: "isRedirected",
	createdat: "createdAt",
	updatedat: "updatedAt"
};

const canonicalizeImportKey = (key: string) => key.replace(/[^a-z0-9]/gi, "").toLowerCase();

export const coerceStringValue = (value: unknown): string | null | undefined => {
	if (value === undefined) {
		return undefined;
	}
	if (value === null) {
		return null;
	}
	if (typeof value === "string") {
		return value;
	}
	if (typeof value === "number" || typeof value === "boolean") {
		return String(value);
	}
	return undefined;
};

export const coerceBooleanValue = (value: unknown): boolean | undefined => {
	if (value === undefined || value === null || value === "") {
		return undefined;
	}
	if (typeof value === "boolean") {
		return value;
	}
	if (typeof value === "number") {
		if (value === 1) return true;
		if (value === 0) return false;
		return undefined;
	}
	if (typeof value === "string") {
		const normalized = value.trim().toLowerCase();
		if (["true", "1", "yes", "y"].includes(normalized)) {
			return true;
		}
		if (["false", "0", "no", "n"].includes(normalized)) {
			return false;
		}
	}
	return undefined;
};

export const coerceStringArrayValue = (value: unknown): string[] | undefined => {
	if (value === undefined || value === null) {
		return undefined;
	}
	if (Array.isArray(value)) {
		return value
			.map((entry) => coerceStringValue(entry))
			.filter((entry): entry is string => typeof entry === "string");
	}

	const single = coerceStringValue(value);
	return typeof single === "string" ? [single] : undefined;
};

export const coerceHeadingsValue = (value: unknown): { [key: string]: string[] } | undefined => {
	if (!value || typeof value !== "object" || Array.isArray(value)) {
		return undefined;
	}

	const normalizedEntries: Record<string, string[]> = {};
	for (const [headingKey, rawList] of Object.entries(value as Record<string, unknown>)) {
		const list = coerceStringArrayValue(rawList);
		if (list && list.length > 0) {
			normalizedEntries[headingKey] = list;
		}
	}

	return Object.keys(normalizedEntries).length > 0 ? normalizedEntries : undefined;
};

export const ensureRelativePath = (value?: string | null) => {
	const trimmed = value?.trim();
	if (!trimmed) {
		return null;
	}

	if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
		try {
			const parsed = new URL(trimmed);
			const composed = `${parsed.pathname}${parsed.search}${parsed.hash}`;
			return composed || "/";
		} catch {
			return null;
		}
	}

	return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
};

export const buildSiteLink = (base: string, relativePath?: string | null) => {
	if (!relativePath) {
		return null;
	}
	const normalizedBase = base.endsWith("/") ? base.slice(0, -1) : base;
	return `${normalizedBase}${relativePath}`;
};

export const normalizeMigrationEntry = (raw: Record<string, unknown>): MigrationUploadObject => {
	const normalized: Record<string, unknown> = {};

	for (const [rawKey, rawValue] of Object.entries(raw)) {
		const targetKey = MIGRATION_FIELD_MAP[canonicalizeImportKey(rawKey)];
		if (!targetKey) {
			normalized[rawKey] = rawValue;
			continue;
		}

		switch (targetKey) {
			case "documentLinks":
			case "downloadedDocuments": {
				const list = coerceStringArrayValue(rawValue);
				if (list) {
					normalized[targetKey] = list;
				}
				break;
			}
			case "headings": {
				const headings = coerceHeadingsValue(rawValue);
				if (headings) {
					normalized[targetKey] = headings;
				}
				break;
			}
			case "isSupported":
			case "isBroken":
			case "isRedirected": {
				const boolValue = coerceBooleanValue(rawValue);
				if (typeof boolValue === "boolean") {
					normalized[targetKey] = boolValue;
				}
				break;
			}
			default: {
				const scalar = coerceStringValue(rawValue);
				if (scalar !== undefined) {
					normalized[targetKey] = scalar;
				}
				break;
			}
		}
	}

	return normalized as MigrationUploadObject;
};

export const statusBadgeClass = (variant: "supported" | "broken" | "redirected" | "skipped") => {
	switch (variant) {
		case "supported":
			return "bg-emerald-50 text-emerald-700";
		case "broken":
			return "bg-rose-50 text-rose-700";
		case "skipped":
			return "bg-amber-50 text-amber-700";
		case "redirected":
		default:
			return "bg-slate-100 text-slate-600";
	}
};

export const ExternalLinkIcon = () => (
	<svg
		className="h-4 w-4"
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true"
	>
		<path d="M14 3h7v7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
		<path d="M10 14L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
		<path
			d="M21 14v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h6"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);
